import { Check } from "lucide-react";
import type { MooMessage } from "../../types/moo";
import type { MooMessagesProps } from "./MooMessages";
import { api } from "../../lib/api";
import { proposalReconciledMonths } from "./moo-client";
import { reconciliationMonthLabel } from "../../lib/reconciliation";
import MooProposedAction from "./MooProposedAction";
import { mooEntryKey } from "./moo-entry-key";

/**
 * The changes one Moo message proposes, and the controls that act on them together: confirm (with
 * a warning first for deletions or reconciled months), retry, or stop what is left of a save.
 */
export default function MooProposal({
  m, data, profile, busy, editing, setEditing, setError, revise, save, openTransaction, setConfirm, chatId, setMessages, onChanged,
}: Pick<
  MooMessagesProps,
  "data" | "profile" | "busy" | "editing" | "setEditing" | "setError" | "revise" | "save"
  | "openTransaction" | "setConfirm" | "chatId" | "setMessages" | "onChanged"
> & { m: MooMessage }) {
  // Only messages that carry a proposal are drawn with this.
  const actions = m.payload.actions ?? [];
  return (
    <div className="moo-proposal">
      <div className="moo-proposal-title">
        {m.payload.status === "saved" ? (
          <>
            <Check size={17} /> {m.payload.undone ? "Undone" : "Saved & verified"}
          </>
        ) : m.payload.status === "superseded" ? (
          "Replaced by a newer message"
        ) : (
          `Review ${actions.length} change${actions.length === 1 ? "" : "s"}: ${[...new Set(actions.map(a => a.kind.replace("expense_", `${a.expense?.type?.toLowerCase() || "expense"} `).replace("price_", "price ").replace("category_", "category ")))].join(", ")}`
        )}
      </div>
      {/* Proposal revisions guard server writes; they must not remount transaction editors. */}
      {m.payload.status !== "superseded" &&
        actions.map((a, index) => (
          <div
            key={a.expense ? mooEntryKey(m.id, a, index) : `${m.id}:${index}:${m.payload.revision}`}
          >
            <MooProposedAction m={m} a={a} index={index} data={data} profile={profile} busy={busy} revise={revise} setEditing={setEditing} setError={setError} openTransaction={openTransaction} />
          </div>
        ))}
      {m.payload.error && (
        <p className="form-error" role="alert">
          {m.payload.error} Completed steps are retained.
          Retry checks the same records.
        </p>
      )}
      {["pending", "saving"].includes(
        m.payload.status || "",
      ) && (
        <button
          className="primary-button"
          disabled={
            !!busy ||
            Object.entries(editing).some(
              ([key, v]) => key.startsWith(m.id) && v,
            )
          }
          onClick={() => {
            const months = proposalReconciledMonths(data, actions);
            const deleting = actions.some((a) => a.kind.endsWith("_delete"));
            if (deleting || months.length) setConfirm({
              title: months.length ? "Reopen a reconciled month?" : "Confirm these changes?",
              message: `${deleting ? "The listed records include deletions. " : ""}${months.length ? `These changes affect ${months.map(reconciliationMonthLabel).join(", ")} and will reopen that month’s balance review.` : "The listed records will be changed."}`,
              danger: deleting,
              confirmLabel: months.length ? "Save anyway" : "Confirm changes",
              onConfirm: () => save(m),
            });
            else void save(m);
          }}
        >
          {m.payload.status === "saving"
            ? "Retry & verify remaining changes"
            : "Confirm changes"}{" "}
          <Check size={18} />
        </button>
      )}
      {m.payload.status === "saving" && (
        <button
          className="moo-edit"
          disabled={!!busy}
          onClick={() =>
            setConfirm({
              title: "Stop the remaining changes?",
              message:
                "Saved steps will remain. Any unverified step may already exist—check History and Prices before adding it again.",
              confirmLabel: "Stop remaining changes",
              onConfirm: async () => {
                const result = await api<{
                  messages: MooMessage[];
                }>("moo/stop", {
                  chatId,
                  messageId: m.id,
                  revision: m.payload.revision,
                });
                setMessages(result.messages);
                onChanged();
              },
            })
          }
        >
          Stop remaining changes
        </button>
      )}
      {m.payload.status === "saved" && !m.payload.undone && m.payload.undoError && (
        <p className="form-error" role="alert">
          {m.payload.undoError} Reverted steps are kept. Retry continues from the first step not yet reverted.
        </p>
      )}
      {m.payload.status === "saved" && !m.payload.undone && !m.payload.undoActions?.length && m.payload.undoNote && (
        <p className="muted">{m.payload.undoNote}</p>
      )}
      {m.payload.status === "saved" && !!m.payload.undoActions?.length && !m.payload.undone && (
        <button
          className="moo-edit"
          disabled={!!busy}
          onClick={() =>
            setConfirm({
              title: "Undo these changes?",
              message: "This will revert the saved changes.",
              confirmLabel: "Undo changes",
              danger: true,
              onConfirm: async () => {
                const result = await api<{
                  messages: MooMessage[];
                }>("moo/undo", {
                  chatId,
                  messageId: m.id,
                  revision: m.payload.revision,
                });
                setMessages(result.messages);
                onChanged();
              },
            })
          }
        >
          {m.payload.undoError ? "Retry undo" : "Undo changes"}
        </button>
      )}
    </div>
  );
}
